import { Box, Button, Divider, Grid, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { SectionHeading } from "~/components/DetailsPage/components";
import { CveRiskFactors, Sources } from "~/components/vulnerabilities";
import { Space } from "~/lib/types";
import { TabsVariant } from "~/components/ui-library";
import { LoadingFailedPage, LoadingPage } from "~/components/loading";
import { ScoreBlock } from "~/pages/space/security/components/Check/ScoreBlock";
import { ExposedAssets } from "~/pages/space/vulnerabilities/components/exposed-assets/ExposedAssets";
import { ScopeType, SpaceOrWorkspaceScope } from "~/hooks/useScope";
import { DetailPageTopSection } from "~/components/DetailPageLayouts/DetailPageTopSection";
import { GenerateReportMenuItem } from "~/components/GenerateReport";
import { getCheckStats } from "./utils";
import { AssetContextualLinkType } from "~/pages/space/vulnerabilities/components/exposed-assets/types";
import { InventoryPageHeader } from "~/routes";
import { TicketsSummary } from "~/pages/tickets/components/TicketsSummary/TicketsSummary";
import { CheckRemediation } from "~/pages/space/security/components/Check/CheckRemediation/CheckRemediation";
import { Flex } from "~/components/Flex";
import { CheckQueriesPropertyItems } from "./CheckQueries/components/CheckQueriesPropertyItems";
import { CheckQueriesMql } from "./CheckQueries/components/CheckQueriesMql";
import { ArrowForward as ArrowForwardIcon } from "@mui/icons-material";
import { useCheck } from "~/pages/space/security/components/Check/hooks/useCheck";
import {
  DocumentType,
  ExceptionType,
  FindingType,
  ScoreRating,
  ScoreStateFilter,
} from "~/operations";

export type CheckProps = {
  space: Space;
  scope: SpaceOrWorkspaceScope;
};

export function Check(props: CheckProps) {
  const { space, scope } = props;
  const {
    isLoading,
    hasError,
    title,
    fallbackPath,
    checkMrn,
    enforcementState,
    previewUntil,
    findingMrn,
    hasException,
    hasTicketingIntegration,
    linkToException,
    hasVariants,
    variants,
    selectedVariant,
    searchParams,
    setSearchParams,
    hasListTicketsPermissions,
    hasCreateTicketPermissions,
    hasTickets,
    aggScores,
    checkScore,
    ticketsCount,
    summary,
    blastRadius,
    policyId,
    hasSources,
    sourceLinks,
    riskFactorsWithDocs,
    visibleQueries,
    linkToPolicyPropertiesTab,
    selectionProps,
    handleRemoveExceptionDialogOpen,
    handleSetExceptionDialogOpen,
    handleCreateTicketDialogOpen,
    handleCreateTicketingIntegrationModalOpen,
    handleSecurityPipelinePRDialogOpen,
  } = useCheck(props);

  if (isLoading) return <LoadingPage what="check details" />;

  if (hasError) return <LoadingFailedPage what="check details" />;

  return (
    <>
      <DetailPageTopSection
        content={{
          triageBar: {
            title,
            fallbackPath,
            additionalMenuItems: (
              <GenerateReportMenuItem
                documentType={DocumentType.CheckReport}
                title={title || "Generated Vulnerability Report"}
                space={space}
                checkId={String(checkMrn)}
              />
            ),
            triage: true,
            isLoading: isLoading,
            availableRemediations: {
              exception: {
                active: scope.type === ScopeType.Space,
                hasException: hasException,
                linkToException,
                onClick: hasException
                  ? handleRemoveExceptionDialogOpen
                  : () =>
                      handleSetExceptionDialogOpen({
                        selectedAssets: [
                          {
                            scopeMrn: scope.mrn,
                            findingMrn: findingMrn,
                            mrn: space?.mrn || "",
                            rating: checkScore?.rating || ScoreRating.Unscored,
                            title: checkScore?.title || "",
                            exception: checkScore?.exception || null,
                          },
                        ],
                      }),
                disabled: isLoading,
              },
              ticket: {
                active: true,
                onClick: hasTicketingIntegration
                  ? () =>
                      handleCreateTicketDialogOpen({
                        isScopeLevelTicket: true,
                        selectedAssets: [
                          {
                            mrn: findingMrn,
                            scopeMrn: scope.mrn,
                            findingMrn: findingMrn,
                            title: checkScore?.title || "",
                            exception: checkScore?.exception || null,
                          },
                        ],
                      })
                  : handleCreateTicketingIntegrationModalOpen,
              },
              securityPipeline: {
                active: false,
                onClick: handleSecurityPipelinePRDialogOpen,
              },
            },
            conversation: {
              scope: scope,
              findingMrn: findingMrn,
              findingType: "checkfinding",
            },
          },
          ...(hasVariants && {
            variantTabs: (
              <Box mt={2}>
                <TabsVariant
                  options={variants}
                  selectedTab={selectedVariant.value}
                  onTabChange={(variantId) => {
                    variantId.length > 0
                      ? searchParams.set("variantQuery", variantId)
                      : searchParams.delete("variantQuery");
                    setSearchParams(searchParams);
                  }}
                  // options={createVariantButtons(check?.variants)}
                  // selectedTab={selectedVariant.value}
                  // onTabChange={onVariantsChange}
                  mb={3}
                  data-testid="check-variants"
                />
              </Box>
            ),
          }),
          ...(hasListTicketsPermissions &&
            hasTickets && {
              ticketsSummary: (
                <TicketsSummary count={ticketsCount} scope={scope} />
              ),
            }),
          sectionHeading: "Check Overview",
          enforcementState: {
            enforcementState,
            previewUntil,
          },
          summary,
          stats: getCheckStats({ blastRadius }),
          left: {
            ...(hasSources && {
              upper: <Sources id="check-sources" links={sourceLinks} />,
            }),
          },
          right: (
            <ScoreBlock
              hasScore={Boolean(checkScore)}
              mainScore={checkScore?.riskValue}
              blastRadius={checkScore?.blastRadius}
              riskFactors={checkScore?.riskFactors}
              rating={checkScore?.rating || ScoreRating.Unscored}
              hasError={aggScores?.__typename !== "AggregateScoresConnection"}
            />
          ),
          openExceptions: {
            scopeMrn: scope.mrn,
            exceptionId: checkScore?.exception?.exceptionId,
            types: [ExceptionType.Security],
          },
        }}
      />

      <InventoryPageHeader disableYPadding>
        {riskFactorsWithDocs.length > 0 && (
          <Grid id="risk-factors" size={12}>
            <SectionHeading heading="Check insights" />
            <Box className="section-content">
              <CveRiskFactors
                id="cve-risk-factors"
                riskFactors={riskFactorsWithDocs}
              />
            </Box>
          </Grid>
        )}

        <SectionHeading heading="Queries" />
        {visibleQueries.map((q, i) => {
          const remediations = q.docs?.remediations;
          const hasRemediations =
            remediations?.__typename === "Remediations" &&
            remediations.entries.length > 0;

          return (
            <Box key={q.mrn}>
              {i > 0 && <Divider sx={{ my: 3 }} />}
              {q.mql && (
                <Grid size={12}>
                  <Flex
                    alignItems="center"
                    justifyContent="space-between"
                    mb={2}
                  >
                    <Typography fontWeight={700}>MQL</Typography>
                  </Flex>
                  <CheckQueriesMql {...{ mql: q.mql }} />
                </Grid>
              )}
              {hasRemediations && (
                <Grid size={12}>
                  <SectionHeading heading="Remediation" />
                  <CheckRemediation entries={remediations.entries} />
                </Grid>
              )}
              {(q.properties?.length || 0) > 0 && (
                <Grid size={12}>
                  <Flex
                    alignItems="center"
                    justifyContent="space-between"
                    my={2}
                  >
                    <Typography fontWeight={700}>Properties</Typography>
                    {policyId && (
                      <Button
                        component={RouterLink}
                        to={linkToPolicyPropertiesTab}
                        endIcon={<ArrowForwardIcon />}
                        sx={{
                          color: "secondary.light",
                        }}
                      >
                        Edit Properties
                      </Button>
                    )}
                  </Flex>
                  <CheckQueriesPropertyItems
                    {...{ properties: q.properties }}
                  />
                </Grid>
              )}
            </Box>
          );
        })}

        {/* <AdvisoryContentDetails advisoryId={advisory.id} /> */}

        {/* <AdvisoryRemediation advisoryId={advisory.id} spaceMrn={space.mrn} /> */}

        {checkMrn && (
          <ExposedAssets
            filter={{
              mrn: checkMrn,
              types: [FindingType.Check],
              state: ScoreStateFilter.All,
            }}
            scope={scope}
            emptyStateMessage="There are currently no exposed assets for this check."
            urlContextType={AssetContextualLinkType.Check}
            contextId={checkMrn}
            onCreateTicketClick={handleCreateTicketDialogOpen}
            onSetExceptionClick={handleSetExceptionDialogOpen}
            onRemoveExceptionClick={handleRemoveExceptionDialogOpen}
            selectionProps={{
              selectedEntities: selectionProps.selectedAssets,
              hasSelectPermissions: hasCreateTicketPermissions,
              onCheckboxClick: selectionProps.handleNodeClick,
              getIsRowSelected: (rowAssetMrn) => {
                return Boolean(
                  selectionProps.selectedAssets.find(
                    (asset) => asset.mrn === rowAssetMrn,
                  ),
                );
              },
              onCheckAll: (checked, edges) => {
                if (checked) {
                  selectionProps.setSelectedAssets(
                    (edges || []).map((edge) => ({
                      groupId: "",
                      scopeMrn: edge?.mrn || "",
                      mrn: edge?.mrn,
                      findingMrn: findingMrn,
                      title: checkScore?.title || "",
                      exception: null,
                    })),
                  );
                } else {
                  selectionProps.setSelectedAssets([]);
                }
              },
              onCheckboxChange: async (_, checked, asset) => {
                const nextSelected = selectionProps.selectedAssets.filter(
                  (s) => s.mrn !== asset?.assetMrn,
                );

                if (checked) {
                  nextSelected.push({
                    mrn: asset.assetMrn,
                    scopeMrn: asset.assetMrn || "",
                    findingMrn: findingMrn,
                    title: checkScore?.title || "",
                    exception: asset.exception || null,
                  });
                }

                selectionProps.setSelectedAssets(nextSelected);
              },
              onCancel: selectionProps.handleCancelClick,
            }}
          />
        )}
      </InventoryPageHeader>
    </>
  );
}
